import {useContext} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'
import LeadPhoneCall, {leadPhoneCallDesigns, leadPhoneCallSizes, leadPhoneCallTarget} from '@adv-mt/mt-lead-phone-call'
import IconDirectionsOutline from '@adv-mt/mt-svg-icons/lib/DirectionsOutline'
import IconLinkExternal from '@adv-mt/mt-svg-icons/lib/LinkExternal'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import FavoriteButton from './FavoriteButton.js'

const commonlLinkProps = {
  target: '_blank',
  rel: 'nofollow noopener',
  link: true,
  size: atomButtonSizes.SMALL,
  negative: true,
  fullWidth: true
}

const externalLinkProps = {
  ...commonlLinkProps,
  leftIcon: <IconDirectionsOutline />
}

const websiteLinkProps = {
  ...commonlLinkProps,
  leftIcon: <IconLinkExternal />
}

const TRACKS = {
  VISIT_WEBSITE: {
    event: 'Website Clicked',
    data: {
      hit_origin: 'Professional Stock Website Clicked'
    }
  },
  DIRECTIONS: {
    event: 'Map Clicked',
    data: {
      hit_origin: 'Dealer Information Map Clicked'
    }
  },
  STOCK_URL_CLICK: {
    event: 'Dealer Stock Clicked',
    data: {
      hit_origin: 'Detail Dealer Stock Clicked'
    }
  }
}

const PanelContactButtons = ({
  adCategoryId,
  adId,
  dealer,
  extendedLeadTrackingData,
  isFavorite,
  isHidePhone,
  isLoggedUser,
  isRentingDealer,
  multiProvinceTrackingData,
  pageName
}) => {
  const {i18n, domain} = useContext(Context)

  const {track} = useAnalyticsTrack()
  const {push} = useRouter()

  const config = domain.get('config')
  const {AUTHENTICATION_HIT_INFORMATIONS_2} = config.get('tracking')

  const isMobile = config.get('isMobile')
  const {externalPageUrl, hasCallTrackingService, id, location, phoneNumber, stockPageUrl} = dealer

  const {mapsDirectionsUrl} = location || {}

  const hasMapsDirectionsUrl = Boolean(mapsDirectionsUrl)

  const handleVisitWeb = () =>
    track({
      ...TRACKS.VISIT_WEBSITE,
      data: {...TRACKS.VISIT_WEBSITE.data, ...extendedLeadTrackingData}
    })

  const handleClickDirectionsUrl = () =>
    track({
      ...TRACKS.DIRECTIONS,
      data: {...TRACKS.DIRECTIONS.data, ...extendedLeadTrackingData}
    })

  const handleClickStockUrl = () => {
    track({...TRACKS.STOCK_URL_CLICK})
    push(`${stockPageUrl}${adCategoryId && !isRentingDealer ? `?Section1Id=${adCategoryId}` : ''}`)
  }

  const getColorAndDesignCTA = () => {
    return {
      leadPhone: {
        color: isMobile ? atomButtonColors.PRIMARY : atomButtonColors.NEUTRAL,
        design: isMobile ? leadPhoneCallDesigns.FLAT : leadPhoneCallDesigns.OUTLINE
      },
      button: {
        color: isMobile ? atomButtonColors.PRIMARY : atomButtonColors.NEUTRAL,
        design: isMobile ? atomButtonDesigns.FLAT : atomButtonDesigns.OUTLINE
      }
    }
  }

  return (
    <div className="mt-HeroDealer-panelButtons">
      {stockPageUrl && (
        <AtomButton
          color={atomButtonColors.PRIMARY}
          design={atomButtonDesigns.SOLID}
          negative={!isMobile}
          size={atomButtonSizes.SMALL}
          onClick={handleClickStockUrl}
          shape={atomButtonShapes.CIRCULAR}
        >
          {i18n.t('COMMON.MORE_SELLER_VEHICLES')}
        </AtomButton>
      )}
      <div className="mt-HeroDealer-panelButtons-containerButton">
        <FavoriteButton
          dealer={dealer}
          isFavorite={isFavorite}
          label={isMobile ? i18n.t('COMMON.ADD_FAVORITE_DEALER') : i18n.t('COMMON.FAVORITE')}
          size={atomButtonSizes.SMALL}
          iconSize={atomIconSizes.small}
          authenticationHitInformation2={AUTHENTICATION_HIT_INFORMATIONS_2.DETAIL}
          trackingExtraData={{
            ...extendedLeadTrackingData,
            hit_origin: 'detail',
            title: extendedLeadTrackingData?.products?.[0]?.title,
            ad_id: extendedLeadTrackingData?.products?.[0]?.id
          }}
        />
      </div>
      {!isHidePhone && (
        <div className="mt-HeroDealer-panelButtons-containerButton">
          <LeadPhoneCall
            adId={adId}
            color={getColorAndDesignCTA().leadPhone.color}
            contractId={id}
            design={getColorAndDesignCTA().leadPhone.design}
            extendedLeadTrackingData={extendedLeadTrackingData}
            hasCallTrackingService={hasCallTrackingService}
            isLoggedUser={isLoggedUser}
            negative={!isMobile}
            pageName={pageName}
            phone={phoneNumber}
            size={leadPhoneCallSizes.small}
            target={adId ? leadPhoneCallTarget.vehicle : leadPhoneCallTarget.dealerHeader}
            text={isMobile ? i18n.t('COMMON_PHONE_CALL_DEALER') : null}
            multiprovince={multiProvinceTrackingData}
            isFullWidth={false}
          />
        </div>
      )}
      {externalPageUrl && (
        <div className="mt-HeroDealer-panelButtons-containerButton">
          <AtomButton
            href={externalPageUrl}
            {...websiteLinkProps}
            onClick={handleVisitWeb}
            design={getColorAndDesignCTA().button.design}
            color={getColorAndDesignCTA().button.color}
            shape={atomButtonShapes.CIRCULAR}
            negative={!isMobile}
            fullWidth={false}
            aria-label={i18n.t('COMMON_SHOW_DEALER_WEBSITE')}
          >
            {isMobile ? i18n.t('COMMON_SHOW_DEALER_WEBSITE') : null}
          </AtomButton>
        </div>
      )}
      {hasMapsDirectionsUrl && (
        <div className="mt-HeroDealer-panelButtons-containerButton--noBorder">
          <AtomButton
            href={mapsDirectionsUrl}
            {...externalLinkProps}
            onClick={handleClickDirectionsUrl}
            design={getColorAndDesignCTA().button.design}
            color={getColorAndDesignCTA().button.color}
            shape={atomButtonShapes.CIRCULAR}
            negative={!isMobile}
            fullWidth={false}
            aria-label={i18n.t('COMMON_LOCATION_DEALER_SHOP')}
          >
            {isMobile ? i18n.t('COMMON_LOCATION_DEALER_SHOP') : null}
          </AtomButton>
        </div>
      )}
    </div>
  )
}

PanelContactButtons.propTypes = {
  adCategoryId: PropTypes.number,
  adId: PropTypes.string,
  dealer: PropTypes.object,
  extendedLeadTrackingData: PropTypes.object,
  isFavorite: PropTypes.bool,
  isHidePhone: PropTypes.bool,
  isLoggedUser: PropTypes.bool,
  isRentingDealer: PropTypes.bool,
  linkFactory: PropTypes.func,
  multiProvinceTrackingData: PropTypes.object,
  pageName: PropTypes.string
}

export default PanelContactButtons
